// Follow-up reminders (migration 082): read my open reminders, set / remove one, mark one
// done. Personal data like watchers, so the admin UI reads and updates it directly; the
// "set" goes through set_ticket_reminder so replacing an old reminder is one atomic step.

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { chunk } from './page-all';
import { supabase } from './supabase';
import type { ReminderLike, ReminderTicket } from './reminders';
import type { Ticket } from './queries';
import { toastFailure, toastSuccess } from './toast';

export type MyReminder = ReminderLike;

const KEY = ['my-reminders'];

/** My open reminders (not cancelled, not done): upcoming ones and ones that already fired. */
export function useMyReminders() {
  return useQuery({
    queryKey: KEY,
    // Short on purpose: a reminder fires on the server, and My Day is where you look for it.
    staleTime: 10_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<MyReminder[]> => {
      const { data, error } = await supabase
        .from('ticket_reminders')
        .select('id, ticket_id, remind_at, only_if_no_reply, note, created_at, fired_at, done_at, cancelled_at')
        .is('cancelled_at', null)
        .is('done_at', null)
        .order('remind_at', { ascending: true })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as MyReminder[];
    },
  });
}

/** The open reminder I have on this ticket, if any. */
export function useTicketReminder(ticketId: string | undefined): MyReminder | null {
  const q = useMyReminders();
  return useMemo(() => (q.data ?? []).find((r) => r.ticket_id === ticketId) ?? null, [q.data, ticketId]);
}

/** Ticket ids that carry one of my open reminders (for the alarm icon on rows). */
export function useReminderTicketIds(): ReadonlySet<string> {
  const q = useMyReminders();
  return useMemo(() => new Set((q.data ?? []).map((r) => r.ticket_id)), [q.data]);
}

/** The tickets behind some reminders (they need not be mine). */
export function useReminderTickets(ids: string[]) {
  return useQuery({
    queryKey: ['reminder-tickets', ids],
    enabled: ids.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<Ticket[]> => {
      const out: Ticket[] = [];
      for (const part of chunk(ids, 100)) {
        const { data, error } = await supabase.from('tickets').select('*').in('id', part);
        if (error) throw error;
        out.push(...((data ?? []) as Ticket[]));
      }
      return out;
    },
  });
}

export function toReminderTickets(tickets: readonly Ticket[] | undefined): Map<string, ReminderTicket> {
  return new Map((tickets ?? []).map((t) => [t.id, { id: t.id, status: t.status, archived_at: t.archived_at }]));
}

function refresh(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: KEY });
  qc.invalidateQueries({ queryKey: ['reminder-tickets'] });
}

export function useSetReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { ticketId: string; remindAt: Date; onlyIfNoReply: boolean; note: string }) => {
      const { error } = await supabase.rpc('set_ticket_reminder', {
        p_ticket_id: a.ticketId,
        p_remind_at: a.remindAt.toISOString(),
        p_only_if_no_reply: a.onlyIfNoReply,
        p_note: a.note.trim() || undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => refresh(qc),
    onError: (e) => toastFailure(e, "Couldn't set the reminder"),
  });
}

export function useClearReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ticket_reminders')
        .update({ cancelled_at: new Date().toISOString(), cancel_reason: 'removed' })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      refresh(qc);
      toastSuccess('Reminder removed');
    },
    onError: (e) => toastFailure(e, "Couldn't remove the reminder"),
  });
}

export function useMarkReminderDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ticket_reminders').update({ done_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => refresh(qc),
    onError: (e) => toastFailure(e, "Couldn't mark that done"),
  });
}

